import type { FC, ReactNode } from 'react'

import { useDictionary } from '@/lib/i18n'
import Eyebrow from '@/components/landing/eyebrow'
import Reveal from '@/components/landing/reveal'
import StageChart from '@/components/landing/stage-chart'

const TimeToFinishSection: FC = (): ReactNode => {
    const t = useDictionary()
    const section = t.timeToFinish

    return (
        <section id='time-to-finish' aria-labelledby='time-to-finish-title' className='relative isolate overflow-hidden py-24 sm:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(160deg,oklch(0.32_0.13_22)_0%,oklch(0.18_0.04_20)_48%,oklch(0.145_0.004_286)_100%)]' />
            <div aria-hidden='true' className='noise pointer-events-none absolute inset-0 -z-10 opacity-[0.06]' />

            <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-16'>
                <div>
                    <Reveal>
                        <Eyebrow tone='light'>
                            {section.eyebrow}
                        </Eyebrow>

                        <h2
                            id='time-to-finish-title'
                            className='display-heading mt-5 font-heading text-6xl font-bold uppercase leading-[0.88] tracking-tight text-white sm:text-7xl lg:text-8xl'
                        >
                            {section.title}
                        </h2>

                        <p className='display-heading mt-4 font-heading text-2xl font-medium uppercase tracking-wide text-white/85 sm:text-3xl'>
                            {section.subtitle}
                        </p>

                        <p className='mt-8 max-w-xl text-base leading-relaxed text-white/75 text-pretty sm:text-lg'>
                            {section.body}
                        </p>
                    </Reveal>

                    <div className='mt-12 grid gap-5 sm:grid-cols-2'>
                        {section.highlights.map((highlight, index) => (
                            <Reveal key={highlight.value} delay={index * 100} className='h-full'>
                                <div className='h-full rounded-2xl border border-white/10 bg-black/35 p-6 backdrop-blur-sm'>
                                    <p className='font-heading text-7xl font-bold leading-none tracking-tight text-white'>
                                        {highlight.value}
                                        <span className='ml-1 text-3xl text-race-bright'>
                                            {highlight.unit}
                                        </span>
                                    </p>

                                    <p className='mt-4 text-sm leading-relaxed text-white/75'>
                                        {highlight.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <Reveal delay={120}>
                    <StageChart />
                </Reveal>
            </div>
        </section>
    )
}

export default TimeToFinishSection
